const ENCRYPTED_PAYLOAD_HEADER = "X-Api-Payload-Encrypted";
const ENCRYPTED_RESPONSE_HEADER = "X-Encrypt-Response";
const ENCRYPTED_AUTH_HEADER = "X-Encrypted-Authorization";

const TAG_BYTES = 16;
const NONCE_BYTES = 12;

let cachedKeyPromise = null;      

function toBase64(bytes) {
  let binary = "";
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i += 1) binary += String.fromCharCode(arr[i]);
  return btoa(binary);
}

function fromBase64(base64) {
  const binary = atob(String(base64 || ""));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

function getCryptoKeyBase64() {
  const raw = String(process.env.REACT_APP_API_CRYPTO_KEY || "").trim();
  if (!raw) return "";

  // Tolerate pasted placeholder wrappers/quotes and whitespace/newlines.
  const unwrapped = raw
    .replace(/^<+|>+$/g, "")
    .replace(/^['"]+|['"]+$/g, "")
    .replace(/\s+/g, "");

  // Accept URL-safe Base64 input and normalize to standard Base64.
  const normalized = unwrapped.replace(/-/g, "+").replace(/_/g, "/");
  const remainder = normalized.length % 4;
  if (remainder === 0) return normalized;
  return normalized + "=".repeat(4 - remainder);
}

export function isApiCryptoEnabled() {
  const explicit = String(process.env.REACT_APP_API_CRYPTO_ENABLED || "").trim().toLowerCase();
  if (explicit) return explicit === "1" || explicit === "true" || explicit === "yes";
  return Boolean(getCryptoKeyBase64());
}

async function importAesKey() {
  if (cachedKeyPromise) return cachedKeyPromise;
  cachedKeyPromise = (async () => {
    const raw = getCryptoKeyBase64();
    if (!raw) throw new Error("Missing REACT_APP_API_CRYPTO_KEY");
    let keyBytes;
    try {
      keyBytes = fromBase64(raw);
    } catch {
      throw new Error(
        "REACT_APP_API_CRYPTO_KEY must be valid Base64 (32-byte key, same value as backend ApiEncryption__Key)"
      );
    }
    if (keyBytes.length !== 32) throw new Error("REACT_APP_API_CRYPTO_KEY must decode to 32 bytes");
    return window.crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, ["encrypt", "decrypt"]);
  })();
  return cachedKeyPromise;
}

export function looksLikeEnvelope(v) {
  return Boolean(
    v &&
      typeof v === "object" &&
      Number(v.v) === 1 &&
      typeof v.n === "string" &&
      typeof v.c === "string" &&
      typeof v.t === "string"
  );
}

export async function encryptToEnvelopeJson(plainJsonString) {
  const key = await importAesKey();
  const nonce = window.crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  const plainBytes = new TextEncoder().encode(String(plainJsonString ?? ""));
  const encrypted = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, key, plainBytes);
  const joined = new Uint8Array(encrypted);
  if (joined.length < TAG_BYTES) throw new Error("Invalid AES-GCM encrypted payload");
  const cipher = joined.slice(0, joined.length - TAG_BYTES);
  const tag = joined.slice(joined.length - TAG_BYTES);
  return JSON.stringify({
    v: 1,
    n: toBase64(nonce),
    c: toBase64(cipher),
    t: toBase64(tag),
  });
}

export async function decryptFromEnvelopeJson(envelopeJsonOrObject) {
  const env =
    typeof envelopeJsonOrObject === "string"
      ? JSON.parse(envelopeJsonOrObject)
      : envelopeJsonOrObject;
  if (!looksLikeEnvelope(env)) throw new Error("Invalid envelope shape");
  const key = await importAesKey();
  const nonce = fromBase64(env.n);
  const cipher = fromBase64(env.c);
  const tag = fromBase64(env.t);
  const combined = new Uint8Array(cipher.length + tag.length);
  combined.set(cipher, 0);
  combined.set(tag, cipher.length);
  const plain = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce }, key, combined);
  return new TextDecoder().decode(plain);
}

export async function encryptTokenToBase64Envelope(jwt) {
  const envJson = await encryptToEnvelopeJson(String(jwt || ""));
  return toBase64(new TextEncoder().encode(envJson));
}

export function applyEncryptionHeaders(headers, { encryptedBody, encryptedResponse, encryptedAuth } = {}) {
  const h = headers || {};
  if (encryptedBody) h[ENCRYPTED_PAYLOAD_HEADER] = "1";
  if (encryptedResponse) h[ENCRYPTED_RESPONSE_HEADER] = "1";
  if (encryptedAuth) h[ENCRYPTED_AUTH_HEADER] = "1";
  return h;
}

export const API_CRYPTO_HEADERS = {
  ENCRYPTED_PAYLOAD_HEADER,
  ENCRYPTED_RESPONSE_HEADER,
  ENCRYPTED_AUTH_HEADER,
};

export function isTimeInfoRequest(configOrUrl) {
  const u = String(
    typeof configOrUrl === "string" ? configOrUrl : configOrUrl?.url || ""
  );
  return /TimeInfo/i.test(u);
}
